import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const TaskInfoPage = () => {
  const [taskInfo, setTaskInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [subcategoryFilter, setSubcategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [taskIdFilter, setTaskIdFilter] = useState('');

  const [sortOrder, setSortOrder] = useState('asc');

  const [showTaskTitle, setShowTaskTitle] = useState(true);
  const [showDescription, setShowDescription] = useState(true);
  const [showGuidelines, setShowGuidelines] = useState(true);

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  const allColumns = [
    { label: 'Task ID', key: 'task_id' },
    { label: 'Category', key: 'category' },
    { label: 'Subcategory', key: 'subcategory' },
    { label: 'Task Title', key: 'task_title' },
    { label: 'Description', key: 'task_description' },
    { label: 'Project ID', key: 'project_id' },
    { label: 'Max Payout', key: 'max_payout' },
    { label: 'Client ID', key: 'client_id' },
    { label: 'Task Status', key: 'task_status' },
    { label: 'Assigned Freelancers', key: 'assigned_freelancers' },
    { label: 'Word Count', key: 'required_word_count' },
    { label: 'Language', key: 'required_language' },
    { label: 'Guidelines', key: 'task_guidelines' },
  ];

  useEffect(() => {
    const fetchTaskInfo = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        window.location.href = '/employee-login';
        return;
      }
      try {
        const response = await axios.get(`${apiBaseURL}/api/task-info`);
        setTaskInfo(response.data);
      } catch (err) {
        setError('Failed to fetch task info.');
      } finally {
        setLoading(false);
      }
    };
    fetchTaskInfo();
  }, [apiBaseURL]);

  if (loading) return <div>Loading task info...</div>;
  if (error) return <div>{error}</div>;

  const data = taskInfo.map((task) => ({
    task_id: task.task_id,
    category: task.category || '—',
    subcategory: task.subcategory || '—',
    task_title: task.task_title || '—',
    task_description: task.task_description || '—',
    project_id: task.project_id || '—',
    max_payout: task.max_payout,
    client_id: task.client_id || '—',
    task_status: task.task_status,
    assigned_freelancers: task.assigned_freelancers.length > 0 ? task.assigned_freelancers.join(', ') : 'None',
    required_word_count: task.required_word_count || '—',
    required_language: task.required_language || '—',
    task_guidelines: task.task_guidelines || '—',
  }));

  const uniqueSubcategories = [...new Set(data.map(item => item.subcategory).filter(sc => sc !== '—'))];
  const uniqueStatuses = [...new Set(data.map(item => item.task_status))];

  const filteredData = data.filter(item => {
    const subcategoryMatch = subcategoryFilter ? item.subcategory === subcategoryFilter : true;
    const statusMatch = statusFilter ? item.task_status === statusFilter : true;
    const taskIdMatch = taskIdFilter ? item.task_id.toString().includes(taskIdFilter) : true;
    return subcategoryMatch && statusMatch && taskIdMatch;
  });

  const sortedData = [...filteredData].sort((a, b) => {
    return sortOrder === 'asc' ? a.task_id - b.task_id : b.task_id - a.task_id;
  });

  const filteredColumns = allColumns.filter(col => {
    if (!showTaskTitle && col.key === 'task_title') return false;
    if (!showDescription && col.key === 'task_description') return false;
    if (!showGuidelines && col.key === 'task_guidelines') return false;
    return true;
  });

  const handleSort = () => setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));

  const downloadExcel = () => {
    const now = new Date();
    const timestamp = now.toLocaleString('en-GB', { hour12: false }).replace(/[/:]/g, '-').replace(', ', '_');
    const filename = `TaskInfo_Report_${timestamp}.xlsx`;

    const header1 = ['OrderMinds Freelancing Solutions LLP'];
    const header2 = ['Task Info Report'];
    const header3 = [`Downloaded on: ${now.toLocaleString('en-IN')}`];

    const sheetData = [
      header1,
      header2,
      header3,
      [],
      filteredColumns.map(col => col.label),
      ...sortedData.map(row => filteredColumns.map(col => row[col.key]))
    ];

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Task Info');
    XLSX.writeFile(wb, filename);
  };

  const Table = ({ columns, data }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto bg-white shadow-md rounded-lg">
        <thead>
          <tr className="bg-gray-200">
            {columns.map((col, idx) => (
              <th
                key={idx}
                className="px-4 py-2 text-left cursor-pointer select-none"
                onClick={col.key === 'task_id' ? handleSort : undefined}
              >
                {col.label}
                {col.key === 'task_id' && (
                  <span className="ml-1 text-xs">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-2 text-center text-gray-500">
                No data available
              </td>
            </tr>
          ) : (
            data.map((row, rowIdx) => (
              <tr key={rowIdx} className="border-b">
                {columns.map((col, colIdx) => (
                  <td
                    key={colIdx}
                    className="px-4 py-2 whitespace-normal break-words text-sm"
                    style={{ maxWidth: '250px' }}
                  >
                    {row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="flex-1 p-6 bg-gray-100" style={{ marginLeft: '16rem' }}>
      <h2 className="text-2xl font-semibold mb-4">Task Info</h2>

      <div className="mb-4 text-gray-700 font-medium">Total Tasks: {sortedData.length}</div>

      <div className="flex flex-wrap gap-4 mb-6 items-end">
        <div>
          <label className="block mb-1 text-sm text-gray-700">Search by Task ID</label>
          <input
            type="text"
            value={taskIdFilter}
            onChange={(e) => setTaskIdFilter(e.target.value)}
            placeholder="Enter Task ID"
            className="border border-gray-300 rounded px-3 py-2"
          />
        </div>

        <div>
          <label className="block mb-1 text-sm text-gray-700">Filter by Subcategory</label>
          <select
            value={subcategoryFilter}
            onChange={(e) => setSubcategoryFilter(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2"
          >
            <option value="">All</option>
            {uniqueSubcategories.map((sub, idx) => (
              <option key={idx} value={sub}>{sub}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block mb-1 text-sm text-gray-700">Filter by Task Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2"
          >
            <option value="">All</option>
            {uniqueStatuses.map((status, idx) => (
              <option key={idx} value={status}>{status}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="toggleTaskTitle"
            checked={showTaskTitle}
            onChange={() => setShowTaskTitle(!showTaskTitle)}
            className="w-4 h-4"
          />
          <label htmlFor="toggleTaskTitle" className="text-sm text-gray-700">Task Title</label>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="toggleDescription"
            checked={showDescription}
            onChange={() => setShowDescription(!showDescription)}
            className="w-4 h-4"
          />
          <label htmlFor="toggleDescription" className="text-sm text-gray-700">Description</label>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="toggleGuidelines"
            checked={showGuidelines}
            onChange={() => setShowGuidelines(!showGuidelines)}
            className="w-4 h-4"
          />
          <label htmlFor="toggleGuidelines" className="text-sm text-gray-700">Guidelines</label>
        </div>

        <button
          onClick={downloadExcel}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Download Excel
        </button>
      </div>

      <Table columns={filteredColumns} data={sortedData} />
    </div>
  );
};

export default TaskInfoPage;
